// src/components/workspace/costing/CostingStudio.tsx
// AI-First Costing Studio - Landing page for pricing management

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Users, Calculator, Package, Zap, AlertCircle,
  CheckCircle, ArrowRight, TrendingUp, Building2, FileText,
  Sparkles, ChevronRight, BarChart3, Target, Clock, Loader2
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { PricingProfileStatus } from './PricingProfileStatus';
import { formatCurrency } from '../../../lib/pricing/utils';

interface ProposalNeedingPricing {
  id: string;
  title: string;
  agency: string;
  dueDate: string;
  pricingStatus: 'not_started' | 'in_progress' | 'complete';
  estimatedValue?: number;
  missingElements: string[];
}

interface PricingStats {
  laborCategories: number;
  indirectRatesConfigured: boolean;
  odcCategories: number;
  proposalsWithPricing: number;
  proposalsNeedingPricing: number;
  averageWrapRate: number | null;
}

export function CostingStudio() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PricingStats | null>(null);
  const [proposalsNeedingPricing, setProposalsNeedingPricing] = useState<ProposalNeedingPricing[]>([]);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    if (profile) {
      loadCostingData();
    }
  }, [profile]);

  const loadCostingData = async () => {
    try {
      setLoading(true);

      // Guard check for profile
      if (!profile?.id) {
        console.error('No profile ID available');
        setLoading(false);
        return;
      }

      console.log('Loading costing data for profile:', profile.id);

      // Load pricing profile stats from the CANONICAL Costing-Studio tables (these are
      // what the editors write and what proposal generation consumes) — not the legacy
      // enhanced_pricing_data, which under-counted and could read stale/empty data.
      const [laborRes, indirectRes, odcRes] = await Promise.all([
        supabase.from('company_labor_categories')
          .select('id', { count: 'exact', head: true }).eq('profile_id', profile.id).eq('is_active', true),
        supabase.from('company_indirect_rates')
          .select('fringe_rate, overhead_rate, ga_rate, profit_rate').eq('profile_id', profile.id).maybeSingle(),
        supabase.from('company_odc_categories')
          .select('id', { count: 'exact', head: true }).eq('profile_id', profile.id).eq('is_active', true),
      ]);
      const laborCount = laborRes.count || 0;
      const indirectRates = indirectRes.data;
      const odcCount = odcRes.count || 0;

      // Load proposals needing pricing - query proposal_templates and sam_contracts separately
      const { data: proposalData, error: proposalError } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (proposalError) {
        console.error('Error loading proposals:', proposalError);
      }

      console.log('Loaded proposals:', proposalData?.length || 0);

      // Get unique notice_ids to fetch contract details
      const noticeIds = [...new Set((proposalData || []).map(p => p.notice_id).filter(Boolean))];

      // Fetch contract details separately
      let contractsMap: Record<string, any> = {};
      if (noticeIds.length > 0) {
        const { data: contractsData } = await supabase
          .from('sam_contracts')
          .select('notice_id, title, agency, response_deadline, funding_amount')
          .in('notice_id', noticeIds);

        if (contractsData) {
          contractsMap = contractsData.reduce((acc: Record<string, any>, c: any) => {
            acc[c.notice_id] = c;
            return acc;
          }, {});
        }
      }

      // Parse pricing status from content_json
      const getPricingStatus = (contentJson: any) => {
        if (!contentJson) return 'not_started';
        const pricing = contentJson.pricing || contentJson.pricingData;
        if (pricing?.clins?.length > 0 && pricing?.laborCategories?.length > 0) return 'complete';
        if (pricing?.laborCategories?.length > 0 || pricing?.indirectRates) return 'in_progress';
        return 'not_started';
      };

      const needsPricing: ProposalNeedingPricing[] = (proposalData || [])
        .filter((p: any) => getPricingStatus(p.content_json) !== 'complete')
        .map((p: any) => {
          const contract = contractsMap[p.notice_id] || {};
          const pricingData = p.content_json?.pricing || p.content_json?.pricingData;
          return {
            id: p.id,
            title: contract.title || p.template_name || 'Untitled',
            agency: contract.agency || 'Unknown',
            dueDate: contract.response_deadline || '',
            pricingStatus: getPricingStatus(p.content_json),
            estimatedValue: contract.funding_amount,
            missingElements: getMissingPricingElements(pricingData)
          };
        });

      const completedCount = (proposalData || []).filter((p: any) => getPricingStatus(p.content_json) === 'complete').length;

      setStats({
        laborCategories: laborCount,
        indirectRatesConfigured: !!indirectRates,
        odcCategories: odcCount,
        proposalsWithPricing: completedCount,
        proposalsNeedingPricing: needsPricing.length,
        averageWrapRate: calculateWrapRate(indirectRates)
      });

      setProposalsNeedingPricing(needsPricing);
    } catch (error) {
      console.error('Error loading costing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMissingPricingElements = (pricingData: any): string[] => {
    const missing: string[] = [];
    if (!pricingData) {
      return ['Labor Categories', 'Indirect Rates', 'CLINs'];
    }
    if (!pricingData.laborCategories?.length) missing.push('Labor Categories');
    if (!pricingData.indirectRates) missing.push('Indirect Rates');
    if (!pricingData.clins?.length) missing.push('CLINs');
    return missing;
  };

  const calculateWrapRate = (indirectRates: any | null): number | null => {
    if (!indirectRates) return null;

    const fringe = indirectRates.fringe_rate || 32;
    const overhead = indirectRates.overhead_rate || 45;
    const ga = indirectRates.ga_rate || 12;
    const profit = indirectRates.profit_rate || 10;

    // Industry-standard compounded wrap rate calculation
    return (1 + fringe / 100) * (1 + overhead / 100) * (1 + ga / 100) * (1 + profit / 100);
  };

  const getPricingStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'text-success bg-success-bg';
      case 'in_progress': return 'text-fit-moderate bg-fit-moderate-bg';
      default: return 'text-bw-muted bg-bw-surface-subtle';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header with AI Banner */}
      <div className="bg-bw-sidebar rounded-xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center mb-2">
              <Sparkles className="h-6 w-6 mr-2" />
              <h1 className="text-2xl font-bold">AI Costing Studio</h1>
            </div>
            <p className="text-blue-100 max-w-2xl">
              Build competitive federal pricing with AI guidance. Configure your pricing profile once,
              then let AI help you create winning price proposals for each opportunity.
            </p>
          </div>
          <button
            onClick={() => navigate('/workspace/costing/wizard')}
            className="flex items-center px-4 py-2 bg-bw-surface text-bw-primary rounded-lg hover:bg-bw-primary-tint transition-colors font-medium"
          >
            <Zap className="h-4 w-4 mr-2" />
            Start Pricing Wizard
          </button>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 rounded-lg p-3">
              <div className="text-2xl font-bold">{stats.laborCategories}</div>
              <div className="text-sm text-blue-100">Labor Categories</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <div className="text-2xl font-bold">
                {stats.indirectRatesConfigured ? (
                  <CheckCircle className="h-6 w-6 text-green-300" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-yellow-300" />
                )}
              </div>
              <div className="text-sm text-blue-100">Indirect Rates</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <div className="text-2xl font-bold">
                {stats.averageWrapRate ? `${stats.averageWrapRate.toFixed(2)}x` : '--'}
              </div>
              <div className="text-sm text-blue-100">Avg Wrap Rate</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <div className="text-2xl font-bold">{stats.proposalsNeedingPricing}</div>
              <div className="text-sm text-blue-100">Need Pricing</div>
            </div>
          </div>
        )}
      </div>

      {/* Pricing Profile Status */}
      <PricingProfileStatus stats={stats} onConfigureRates={() => navigate('/workspace/costing/wizard')} />

      {/* Main Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Pricing Components */}
        <div className="col-span-2 space-y-6">
          {/* Component Cards */}
          <div className="grid grid-cols-3 gap-4">
            {/* Labor Categories Card */}
            <button
              onClick={() => navigate('/workspace/costing/labor')}
              className="p-4 bg-bw-surface rounded-xl border border-bw-border hover:border-servet-blue hover:shadow-card transition-all text-left group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-bw-primary-tint rounded-lg">
                  <Users className="h-5 w-5 text-bw-primary" />
                </div>
                <ChevronRight className="h-4 w-4 text-bw-muted group-hover:text-bw-primary transition-colors" />
              </div>
              <h3 className="font-semibold text-bw-ink">Labor Categories</h3>
              <p className="text-sm text-bw-muted mt-1">
                {stats?.laborCategories || 0} categories configured
              </p>
              <div className="flex items-center mt-3 text-xs text-bw-primary">
                <Sparkles className="h-3 w-3 mr-1" />
                AI suggests GSA rates
              </div>
            </button>

            {/* Indirect Rates Card */}
            <button
              onClick={() => navigate('/workspace/costing/indirect-rates')}
              className="p-4 bg-bw-surface rounded-xl border border-bw-border hover:border-servet-blue hover:shadow-card transition-all text-left group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-success-bg rounded-lg">
                  <Calculator className="h-5 w-5 text-success" />
                </div>
                <ChevronRight className="h-4 w-4 text-bw-muted group-hover:text-bw-primary transition-colors" />
              </div>
              <h3 className="font-semibold text-bw-ink">Indirect Rates</h3>
              <p className="text-sm text-bw-muted mt-1">
                {stats?.indirectRatesConfigured ? 'Configured' : 'Not configured'}
              </p>
              <div className="flex items-center mt-3 text-xs text-bw-primary">
                <Sparkles className="h-3 w-3 mr-1" />
                AI benchmarks rates
              </div>
            </button>

            {/* ODC Categories Card */}
            <button
              onClick={() => navigate('/workspace/costing/odc')}
              className="p-4 bg-bw-surface rounded-xl border border-bw-border hover:border-servet-blue hover:shadow-card transition-all text-left group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-servet-red/10 rounded-lg">
                  <Package className="h-5 w-5 text-bw-accent" />
                </div>
                <ChevronRight className="h-4 w-4 text-bw-muted group-hover:text-bw-primary transition-colors" />
              </div>
              <h3 className="font-semibold text-bw-ink">ODC Categories</h3>
              <p className="text-sm text-bw-muted mt-1">
                {stats?.odcCategories || 0} categories
              </p>
              <div className="flex items-center mt-3 text-xs text-bw-primary">
                <Sparkles className="h-3 w-3 mr-1" />
                AI estimates costs
              </div>
            </button>
          </div>

          {/* Wrap Rate Calculator */}
          <div className="bg-bw-surface rounded-xl border border-bw-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <BarChart3 className="h-5 w-5 text-bw-primary mr-2" />
                <h3 className="font-semibold text-bw-ink">Wrap Rate Calculator</h3>
              </div>
              <button
                onClick={() => navigate('/workspace/costing/wrap-rate')}
                className="text-sm text-bw-primary hover:text-bw-primary"
              >
                Configure →
              </button>
            </div>

            {stats?.indirectRatesConfigured ? (
              <div className="space-y-4">
                {/* Wrap Rate Visualization */}
                <div className="flex items-center space-x-2">
                  <div className="flex-1 h-8 bg-bw-primary-tint0 rounded-l flex items-center justify-center text-white text-xs font-medium">
                    Base Rate
                  </div>
                  <div className="w-16 h-8 bg-success-bg0 flex items-center justify-center text-white text-xs font-medium">
                    Fringe
                  </div>
                  <div className="w-20 h-8 bg-fit-moderate-bg0 flex items-center justify-center text-white text-xs font-medium">
                    Overhead
                  </div>
                  <div className="w-14 h-8 bg-orange-500 flex items-center justify-center text-white text-xs font-medium">
                    G&A
                  </div>
                  <div className="w-12 h-8 bg-servet-red rounded-r flex items-center justify-center text-white text-xs font-medium">
                    Fee
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-bw-muted">$100.00 base</span>
                  <span className="font-semibold text-bw-ink">
                    = ${stats.averageWrapRate ? (100 * stats.averageWrapRate).toFixed(2) : '---'} wrapped
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Calculator className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-bw-muted">Configure indirect rates to see wrap rate</p>
                <button
                  onClick={() => navigate('/workspace/costing/wizard')}
                  className="mt-3 text-sm text-bw-primary hover:text-bw-primary"
                >
                  Start Pricing Wizard →
                </button>
              </div>
            )}
          </div>

          {/* Price-to-Win Analysis */}
          <div className="bg-bw-surface-subtle rounded-xl border border-servet-blue/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Target className="h-5 w-5 text-bw-primary mr-2" />
                <h3 className="font-semibold text-bw-ink">Price-to-Win Analysis</h3>
                <span className="ml-2 px-2 py-0.5 bg-bw-primary/10 text-bw-primary text-xs rounded-full">
                  AI Powered
                </span>
              </div>
            </div>
            <p className="text-sm text-bw-muted mb-4">
              AI analyzes historical contract awards, incumbent pricing, and market data to suggest
              competitive price points for each opportunity.
            </p>
            <button
              onClick={() => navigate('/workspace/costing/price-to-win')}
              className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-bw-primary/90 transition-colors text-sm"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Analyze Opportunity
            </button>
          </div>
        </div>

        {/* Right Panel - Proposals Needing Pricing */}
        <div className="space-y-6">
          <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-bw-ink">Proposals Needing Pricing</h3>
              <span className="px-2 py-0.5 bg-fit-moderate-bg text-fit-moderate text-xs rounded-full">
                {proposalsNeedingPricing.length} pending
              </span>
            </div>

            {proposalsNeedingPricing.length > 0 ? (
              <div className="space-y-3">
                {proposalsNeedingPricing.map((proposal) => (
                  <div
                    key={proposal.id}
                    className="p-3 bg-bw-surface-subtle rounded-lg hover:bg-bw-surface-subtle transition-colors cursor-pointer"
                    onClick={() => navigate(`/workspace/${proposal.id}/pricing`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-bw-ink truncate">
                          {proposal.title}
                        </h4>
                        <p className="text-xs text-bw-muted mt-0.5">
                          {proposal.agency}
                        </p>
                      </div>
                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${getPricingStatusColor(proposal.pricingStatus)}`}>
                        {proposal.pricingStatus === 'not_started' ? 'Not Started' :
                         proposal.pricingStatus === 'in_progress' ? 'In Progress' : 'Complete'}
                      </span>
                    </div>

                    {proposal.missingElements.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {proposal.missingElements.map((element, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-urgent-bg text-urgent text-xs rounded">
                            Missing: {element}
                          </span>
                        ))}
                      </div>
                    )}

                    {proposal.estimatedValue && (
                      <div className="mt-2 flex items-center text-xs text-bw-muted">
                        <DollarSign className="h-3 w-3 mr-1" />
                        Est. {formatCurrency(proposal.estimatedValue)}
                      </div>
                    )}

                    {proposal.dueDate && (
                      <div className="mt-1 flex items-center text-xs text-bw-muted">
                        <Clock className="h-3 w-3 mr-1" />
                        Due: {new Date(proposal.dueDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="h-10 w-10 text-success mx-auto mb-2" />
                <p className="text-sm text-bw-muted">All proposals have pricing!</p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-bw-surface rounded-xl border border-bw-border p-4">
            <h3 className="font-semibold text-bw-ink mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/workspace/costing/wizard')}
                className="w-full flex items-center justify-between p-3 bg-bw-primary/5 rounded-lg hover:bg-bw-primary/10 transition-colors text-left"
              >
                <div className="flex items-center">
                  <Zap className="h-4 w-4 text-bw-primary mr-2" />
                  <span className="text-sm font-medium text-bw-ink">Run Pricing Wizard</span>
                </div>
                <ArrowRight className="h-4 w-4 text-bw-muted" />
              </button>

              <button
                onClick={() => navigate('/workspace/costing/clin-builder')}
                className="w-full flex items-center justify-between p-3 bg-bw-surface-subtle rounded-lg hover:bg-bw-surface-subtle transition-colors text-left"
              >
                <div className="flex items-center">
                  <FileText className="h-4 w-4 text-bw-muted mr-2" />
                  <span className="text-sm font-medium text-bw-ink">CLIN Builder</span>
                </div>
                <ArrowRight className="h-4 w-4 text-bw-muted" />
              </button>

              <button
                onClick={() => navigate('/profile')}
                className="w-full flex items-center justify-between p-3 bg-bw-surface-subtle rounded-lg hover:bg-bw-surface-subtle transition-colors text-left"
              >
                <div className="flex items-center">
                  <Building2 className="h-4 w-4 text-bw-muted mr-2" />
                  <span className="text-sm font-medium text-bw-ink">Update Company Profile</span>
                </div>
                <ArrowRight className="h-4 w-4 text-bw-muted" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
